import type { PropTypes } from './ExportPrivateKeyModal.type';
import type { ReactElement } from 'react';

import { Button, Callout, Checkbox, Dialog, Flex, IconButton, Text } from '@rango-dev/whale';
import { Sheet } from '@rango-dev/whale/lab';
import { X } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '../../../hooks/reduxHooks';
import { useWindowDimensions } from '../../../hooks/useWindowDimensions';

import styles from './ExportPrivateKeyModal.module.css';

const DIALOG_MAX_WIDTH = { initial: '100vw', sm: '440px' };
const ILLUSTRATION_SIZE = 107;
const CLOSE_ICON_SIZE = 16;

/** Dismissible confirmation: a centered Radix Dialog on desktop, the `Sheet` on mobile. */
export function ExportPrivateKeyModal(props: PropTypes): ReactElement {
  const { open, onClose, onConfirm } = props;
  const { isDarkMode } = useAppSelector((state) => state.settings);
  const { isMobile } = useWindowDimensions();
  const { t } = useTranslation();

  const [confirmationChecked, setConfirmationChecked] = useState(false);

  const toggleConfirmationCheckbox = () => setConfirmationChecked((prevState) => !prevState);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      onClose();
    }
  };

  const title = t('Export Private key');

  const body = (
    <Flex direction="column" gap="5">
      <Flex justify="center">
        <img
          src={`/imgs/wallets/export-private-key-${isDarkMode ? 'dark' : 'light'}.svg`}
          alt={t('export private key')}
          width={ILLUSTRATION_SIZE}
          height={ILLUSTRATION_SIZE}
          className={styles.illustration}
        />
      </Flex>

      <Callout.Root size="1" color="amber">
        <Callout.Text>
          {t(
            'Export your private key now and store it in a secure location. Do not share it with anyone. Rango is not responsible for loss of your private key.',
          )}
        </Callout.Text>
      </Callout.Root>

      <Text as="label" size="2" color="gray">
        <Flex align="start" gap="2">
          <Checkbox
            id="export-private-key-modal-confirmation-checkbox"
            size="2"
            checked={confirmationChecked}
            onCheckedChange={toggleConfirmationCheckbox}
          />
          {t('I understand the importance of keeping my private key secure.')}
        </Flex>
      </Text>
    </Flex>
  );

  const confirmButton = (
    <Button
      id="export-private-key-modal-confirm-btn"
      size="3"
      radius="full"
      className={styles.confirmButton}
      disabled={!confirmationChecked}
      onClick={onConfirm}
    >
      {t('Export private key')}
    </Button>
  );

  if (isMobile) {
    return (
      <Sheet
        open={open}
        onOpenChange={handleOpenChange}
        idPrefix="export-private-key-modal"
        title={title}
        closeLabel={t('Close') || 'Close'}
        footer={confirmButton}
      >
        {body}
      </Sheet>
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content size="3" maxWidth={DIALOG_MAX_WIDTH} aria-describedby={undefined}>
        <Flex direction="column" gap="4">
          <Flex align="center" justify="between" gap="2">
            <Dialog.Title mb="0" size="5">
              {title}
            </Dialog.Title>
            <Dialog.Close>
              <IconButton
                id="export-private-key-modal-close-btn"
                size="1"
                variant="ghost"
                color="gray"
                radius="full"
                aria-label={t('Close') || 'Close'}
              >
                <X size={CLOSE_ICON_SIZE} />
              </IconButton>
            </Dialog.Close>
          </Flex>
          {body}
          {confirmButton}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
